import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { BrowserWindowSymbol, ExperimentsSymbol, LoggerSymbol, SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import type { BrowserWindow, Experiments, ILogger } from '@wix/thunderbolt-symbols'
import { name, ReactTemplateSymbol, EsModuleShimsRuntimeStateSymbol } from './symbols'
import type { BuilderModuleLoaderSiteConfig, EsModuleShimsRuntimeState, ImportMap, ReactTemplate } from './types'

// Served from Wix static CDN (thunderbolt-shims-static)
const USE_SYNC_EXTERNAL_STORE_SHIM_URL =
	'https://static.parastorage.com/services/thunderbolt-shims-static/1.0.0/use-sync-external-store-shim.js'

function createModuleUrl(code: string) {
	const blob = new Blob([code], { type: 'text/javascript' })
	return URL.createObjectURL?.(blob) || ''
}

declare global {
	interface Window {
		importShim: {
			(url: string): Promise<any>
		}
	}
}

export const ImportMapFactory = withDependencies(
	[
		ReactTemplateSymbol,
		BrowserWindowSymbol,
		ExperimentsSymbol,
		named(SiteFeatureConfigSymbol, name),
		EsModuleShimsRuntimeStateSymbol,
		LoggerSymbol,
	],
	(
		reactTemplate: ReactTemplate,
		browserWindow: BrowserWindow,
		experiments: Experiments,
		siteConfig: BuilderModuleLoaderSiteConfig,
		esModuleShimsRuntimeState: EsModuleShimsRuntimeState,
		logger: ILogger
	): ImportMap => {
		const isEditor = process.env.PACKAGE_NAME === 'thunderbolt-ds'
		let importMapComitted = false
		const shouldDisableBuilderWixComponents = !!experiments['specs.thunderbolt.disableBuilderWixComponents']
		const { esm: packageUrls } = siteConfig.builderComponentsData.publicPackagesUrls

		let pendingImportMap: Record<string, string> = {
			react: createModuleUrl(reactTemplate.getReactModuleCode('window.React')),
			'react-dom': createModuleUrl(reactTemplate.getReactDOMModuleCode('window.ReactDOM')),
			'react/jsx-runtime': createModuleUrl(reactTemplate.getReactJsxRuntimeCode()),
			'react/jsx-dev-runtime': createModuleUrl(reactTemplate.getReactJsxDevRuntimeCode()),
			'use-sync-external-store/shim/index.js': USE_SYNC_EXTERNAL_STORE_SHIM_URL,

			'@wix/builder-interactions': packageUrls['builder-interactions'],
			'@wix/builder-state': packageUrls['builder-state'],
			'@wix/builder-state-react': packageUrls['builder-state-react'],
			'@wix/builder-velo': packageUrls['builder-velo'],
			'@wix/builder-velo-react': packageUrls['builder-velo-react'],
			...(!shouldDisableBuilderWixComponents && {
				'@wix/builder-wix-components': packageUrls['builder-wix-components'],
			}),
			'@wix/services-manager-react': packageUrls['services-manager-react'],
			'@wix/builder-element-removal': packageUrls['builder-element-removal'],
			'@wix/builder-utils': packageUrls['builder-utils'],
		}

		// tracks import specifiers that have been committed to the DOM import map to avoid duplicate commits
		const committedImportSpecifiers = new Set<string>(Object.keys(pendingImportMap))

		let commitId = 0
		const mapScriptId = 'builder-module-loader-importmap'

		const updateImportMap = (updates: Record<string, string>) => {
			Object.entries(updates).forEach(([key, url]) => {
				if (!committedImportSpecifiers.has(key)) {
					pendingImportMap[key] = url
				}
			})
		}

		// creates a new import map (existing import maps mutation is not supported)
		const commitImportMapUpdates = async () => {
			if (!browserWindow?.document) {
				return
			}
			// in public sites we commit the import map only once because some browsers do not support multiple import maps and we don't install the shim in public sites
			if (importMapComitted && !isEditor) {
				logger.captureError(
					new Error(
						'Import maps can only be committed once on public sites. To extend the import map, register dependencies via ImportMapProviderSymbol.'
					),
					{
						tags: {
							feature: 'builder-module-loader',
							methodName: 'commitImportMapUpdates',
						},
					}
				)
			}
			importMapComitted = true
			if (Object.keys(pendingImportMap).length === 0) {
				// nothing to commit
				return
			}

			const mapScript = browserWindow.document.createElement('script')
			mapScript.id = `${mapScriptId}-${commitId}`
			// EsModuleShimsRuntimeState represents an editor-only runtime decision.
			// It defaults to false (always false in public) and is set during appWillMount (editor only) when shims are required.
			mapScript.type = esModuleShimsRuntimeState.isEnabled() ? 'importmap-shim' : 'importmap'
			mapScript.textContent = JSON.stringify({ imports: pendingImportMap })
			browserWindow.document.head.appendChild(mapScript)

			Object.keys(pendingImportMap).forEach((specifier) => committedImportSpecifiers.add(specifier))

			pendingImportMap = {}
			commitId++
			await Promise.resolve()
		}

		const updateAndCommit = (dependencies: Record<string, string>) => {
			updateImportMap(dependencies)
			commitImportMapUpdates()
		}

		return {
			updateImportMap,
			commitImportMapUpdates,
			updateAndCommit,
		}
	}
)
